import {
  ComponentPropsWithoutRef,
  ReactNode,
  useId,
  useMemo,
  useState
} from 'react'
import { Button } from '~shared/components/button-v2'
import { Input } from '~shared/components/input'
import { PhoneInput } from '~shared/components/phone-input'
import { sendLead } from '../model/api'
import { cn } from '@/modules/lib/utils'
import { useRouter } from 'next/router'
import { Checkbox } from '~shared/components/checkbox'

const Field = ({
  error,
  // message,
  children
}: {
  error?: string
  message?: string
  children: ReactNode
}) => {
  return (
    <div>
      {children}
      {error && <span className='text-danger text-[14px] mt-1'>{error}</span>}
    </div>
  )
}

type Schema = {
  name: string
  phoneNumber: string
  email: string
  promocode: string
}
// no need for hookform so far
export interface LeadFormProps extends ComponentPropsWithoutRef<'form'> {
  onSuccess?: () => void
  onError?: () => void
  inputClassName?: string
  buttonText?: string
}

export const LeadForm = (props: LeadFormProps) => {
  const {
    className = '',
    inputClassName = '',
    onSuccess,
    onError,
    buttonText,
    ...rest
  } = props

  const [values, setValues] = useState<Schema>({
    name: '',
    phoneNumber: '',
    email: '',
    promocode: ''
  })
  const [errors, setErrors] = useState<Schema>({
    name: '',
    phoneNumber: '',
    email: '',
    promocode: ''
  })

  const isError = useMemo(() => Object.values(errors).some(v => !!v), [errors])
  const [isLoading, setIsLoading] = useState(false)

  const mailingCheckboxId = useId()

  const handleChange = (name: string, value: string | null) => {
    setValues(prev => ({ ...prev, [name]: value }))
    setErrors(prev => ({ ...prev, [name]: '' }))
  }

  const router = useRouter()

  const validateForm = () => {
    let isValid = true
    const newErrors: Partial<Schema> = {}

    if (!values.phoneNumber) {
      newErrors.phoneNumber = 'Некорректный номер телефона'
      isValid = false
    }

    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/
    if (values.email && !emailRegex.test(values.email)) {
      newErrors.email = 'Введите корректный email'
      isValid = false
    }

    setErrors(prev => ({ ...prev, ...newErrors }))
    return isValid
  }

  const handleSendForm = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) return

    setIsLoading(true)
    try {
      await sendLead(values)

      onSuccess?.()
      router.push('/thank-you')
    } catch (e) {
      onError?.()
    }

    setIsLoading(false)
  }

  return (
    <form className={className} onSubmit={handleSendForm} {...rest}>
      <div className='grid grid-cols-1 md:grid-cols-2 gap-[8px] md:gap-[16px] w-full'>
        <Field error={errors.name}>
          <Input
            isError={!!errors.name}
            placeholder='Введите ваше имя'
            name='name'
            value={values.name}
            onChange={e => handleChange(e.target.name, e.target.value)}
            className={cn('w-full', inputClassName)}
          />
        </Field>
        <Field error={errors.phoneNumber}>
          <PhoneInput
            isError={!!errors.phoneNumber}
            name='phoneNumber'
            value={values.phoneNumber}
            onChange={(phone, e) => handleChange(e.target.name, phone)}
            className={cn('w-full', inputClassName)}
          />
        </Field>
        <Field error={errors.email}>
          <Input
            isError={!!errors.email}
            placeholder='Введите вашу почту'
            name='email'
            value={values.email}
            onChange={e => handleChange(e.target.name, e.target.value)}
            className={cn('w-full', inputClassName)}
          />
        </Field>
        <Field error={errors.promocode}>
          <Input
            isError={!!errors.promocode}
            placeholder='Введите промокод'
            name='promocode'
            value={values.promocode}
            onChange={e => handleChange(e.target.name, e.target.value)}
            className={cn('w-full', inputClassName)}
          />
        </Field>
      </div>
      <div className='mt-[16px] md:mt-[24px]'>
        <p className='text-[#8B8B8B] cursor-pointer leading-[1.2] md:leading-[1.1] text-[10px] md:text-[12px]'>
          Нажимая кнопку, я соглашаюсь на обработку моих персональных данных
          в&nbsp;соответствии с&nbsp;
          <a
            href='/policies/ДПО_МИТУ_Политика_в_отношении_обрабтки_персональных_данных.pdf'
            className='underline cursor-pointer text-tertiary'>
            Политикой конфиденциальности
          </a>
        </p>
        <div className='cursor-pointer flex items-center gap-[16px] mt-[16px]'>
          <Checkbox
            name='mailingAgreement'
            id={mailingCheckboxId}
            defaultChecked
          />
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label
            htmlFor={mailingCheckboxId}
            className='text-[#8B8B8B] cursor-pointer leading-[1.2] md:leading-[1.1] text-[10px] md:text-[12px]'>
            Я согласен получать рекламу и звонки
          </label>
        </div>
      </div>
      <Button
        type='submit'
        variant='secondary'
        className='h-[65px] md:h-[80px] mt-[16px] w-full'
        disabled={isLoading || isError}>
        {buttonText ?? 'Получить подробный учебный план'}
      </Button>
    </form>
  )
}
